import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, MapPin } from "lucide-react";
import { MaskLine } from "./Reveal";
import { CONTACT, WHATSAPP_MSG, HERO_IMG, COMPLIANCE } from "../../data/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden blueprint-grid"
    >
      {/* Parallax image panel (right) */}
      <motion.div
        style={{ y, scale }}
        className="absolute top-0 right-0 h-full w-full lg:w-[46%] z-0"
      >
        <img src={HERO_IMG} alt="Condomínio moderno à noite" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-ink/10 lg:from-ink lg:via-ink/30 lg:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 sm:px-8 pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-coral" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-coral flex items-center gap-2">
            <MapPin size={13} /> Baixada Santista · SP
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tighter text-white leading-[0.92] text-[13vw] sm:text-[9vw] lg:text-[6.6rem]">
          <MaskLine delay={0.15}>Engenharia</MaskLine>
          <MaskLine delay={0.28}>elétrica que</MaskLine>
          <MaskLine delay={0.41}>
            <span className="text-gradient-coral">resolve.</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-muted-fg leading-relaxed"
        >
          Soluções completas para condomínios residenciais e comerciais. Mais do que
          executar serviços, entregamos <span className="text-white">segurança, tranquilidade
          e respaldo técnico</span> para síndicos e administradoras.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={`https://wa.me/${CONTACT.phoneRaw}?text=${WHATSAPP_MSG}`}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-3 bg-coral hover:bg-coral-hover text-white font-semibold px-7 py-4 transition-colors duration-300 glow-coral"
          >
            Agendar visita técnica
            <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <a
            href="#servicos"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-3 border border-white/20 hover:border-coral text-white font-semibold px-7 py-4 transition-colors duration-300"
          >
            Ver serviços
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-white/10 pt-6"
        >
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-fg">Conformidade:</span>
          {COMPLIANCE.map((c) => (
            <span key={c} data-testid={`compliance-${c}`} className="font-mono text-xs text-white/80 border border-white/15 px-2.5 py-1">
              {c}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
